import { useMemo, useState, useEffect } from 'react';
import type { Community } from '@/types';
import { Skeleton } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { useAuthModal } from '@/features/auth/authModalStore';
import { cn } from '@/lib/utils';
import { useCommunities, useDailyJoins } from '../hooks/useCommunities';
import { CommunityCard } from './CommunityCard';
import { CategoryTabs } from './CategoryTabs';
import { ExploreSearch } from './ExploreSearch';
import { CuratedFeed } from './CuratedFeed';
import { CreateCommunityModal } from './CreateCommunityModal';
import { CATEGORIES } from '../categories';
import { Check, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

type SortId = 'active' | 'newest' | 'members' | 'name';

const SORTS: { id: SortId; label: string }[] = [
  { id: 'active', label: 'Most active' },
  { id: 'newest', label: 'Newest' },
  { id: 'members', label: 'Most members' },
  { id: 'name', label: 'Name (A–Z)' },
];

/** Sorts the visible directory list. "Most active" uses real 24h joins, then
 *  falls back to member count so ties are stable. */
function sortCommunities(
  list: Community[],
  sort: SortId,
  dailyJoins: Record<string, number>,
): Community[] {
  const arr = [...list];
  switch (sort) {
    case 'newest':
      return arr.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    case 'members':
      return arr.sort((a, b) => (b.member_seq || 0) - (a.member_seq || 0));
    case 'name':
      return arr.sort((a, b) => a.name.localeCompare(b.name));
    case 'active':
    default:
      return arr.sort(
        (a, b) =>
          (dailyJoins[b.id] || 0) - (dailyJoins[a.id] || 0) ||
          (b.member_seq || 0) - (a.member_seq || 0),
      );
  }
}

/** Responsive community grid — matches the larger My Communities card footprint.
 *  Shared by the directory grid and the curated feed. */
export const DIRECTORY_GRID =
  'grid grid-cols-1 auto-rows-fr gap-x-5 gap-y-7 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4';

/** Whether a community belongs to a category id. Matches the OG's loose
 *  comparison (id form 'business' OR display form 'AI & Tech'). */
function matchesCategory(c: Community, catId: string): boolean {
  if (catId === 'all') return true;
  const cc = (c.category ?? '').toLowerCase().trim();
  const cat = CATEGORIES.find((x) => x.id === catId);
  return cc === catId || (!!cat && cc === cat.name.toLowerCase());
}

/**
 * The Explore directory — compass header, category tabs, search,
 * curated CTA, and the filtered community grid. Ported from the OG `renderHome`
 * directory block (js/app.js:1427) + `_explore*` (2400). Lives on the home page,
 * below the featured marquee — the OG's one-page model.
 */
export function CommunityDirectory() {
  const { data, isLoading, isError } = useCommunities(48);
  const { user } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortId>('active');
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Reset page when filters or sort change
  useEffect(() => {
    setPage(1);
  }, [category, search, sort]);

  const clearFilters = () => {
    setCategory('all');
    setSearch('');
  };

  const communities = useMemo(() => data ?? [], [data]);
  const { data: dailyJoins = {} } = useDailyJoins(communities.map((c) => c.id));

  const filtered = useMemo(() => {
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return communities.filter((c) => {
      if (!matchesCategory(c, category)) return false;
      if (!tokens.length) return true;
      const hay = [c.name, c.description, c.tagline, c.category]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [communities, category, search]);

  const sorted = useMemo(
    () => sortCommunities(filtered, sort, dailyJoins),
    [filtered, sort, dailyJoins],
  );

  const showCurated = category === 'all' && !search.trim();

  const gridItems = useMemo(() => {
    if (!showCurated || !user) return sorted;

    const picks = [...communities]
      .sort((a, b) => (a.id === 'winr-official' ? -1 : b.id === 'winr-official' ? 1 : 0))
      .slice(0, 4);

    const pickIds = new Set(picks.map((p) => p.id));
    return sorted.filter((c) => !pickIds.has(c.id));
  }, [sorted, showCurated, user, communities]);

  const CARDS_PER_PAGE = 8;
  const totalPages = Math.ceil(gridItems.length / CARDS_PER_PAGE);
  const pagedItems = gridItems.slice((page - 1) * CARDS_PER_PAGE, page * CARDS_PER_PAGE);
  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return Array.from({ length: 5 }, (_, i) => start + i);
  }, [page, totalPages]);

  const goToPage = (nextPage: number) => {
    setPage(Math.max(1, Math.min(totalPages, nextPage)));
    document.getElementById('explore')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="relative left-1/2 min-h-screen ml-[-62.5vw] w-[125vw] pb-32 pt-12"
      id="explore"
    >
      <div className="mx-auto w-full max-w-[100vw] px-5 sm:px-10">
        <div className="mb-6 flex justify-center text-center">
          <div>
            <h2 className="m-0 text-[34px] font-black leading-none text-white sm:text-[40px]">
              Explore communities
            </h2>
          </div>
        </div>

        <div className="mx-auto mb-8 flex w-full max-w-[980px] flex-col items-center justify-center gap-4 lg:flex-row lg:gap-6">
          <ExploreSearch value={search} onChange={setSearch} className="w-full sm:w-[280px]" />
          <CategoryTabs active={category} onSelect={setCategory} className="min-w-0 flex-1" />
          <div className="flex justify-center">
            <SortMenu value={sort} onChange={setSort} />
          </div>
        </div>

        {showCurated && <CuratedFeed communities={communities} onLogin={openAuth} />}

        {isError && (
          <p className="mb-6 text-sm font-semibold text-red-400">
            Couldn&apos;t load communities. Try again.
          </p>
        )}

        <div className={DIRECTORY_GRID}>
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-[430px] rounded-md" />
            ))
          ) : pagedItems.length ? (
            pagedItems.map((c) => <CommunityCard key={c.id} community={c} dailyJoins={dailyJoins[c.id] ?? 0} />)
          ) : (
            <div className="col-span-full flex flex-col items-center gap-1.5 px-5 py-[60px] text-center">
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-white/[0.03] text-[#8f96a3]">
                <svg
                  viewBox="0 0 24 24"
                  width="32"
                  height="32"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              </div>
              <div className="text-[17px] font-extrabold text-white">
                {search.trim() ? `No matches for "${search.trim()}"` : 'Nothing here yet'}
              </div>
              <div className="text-[13px] text-[#8f96a3]">
                {search.trim()
                  ? 'Try a different search or clear your filters.'
                  : 'Pick another category to see more.'}
              </div>
              <div className="mt-3.5 flex flex-wrap justify-center gap-2">
                <button
                  className="cursor-pointer rounded-pill border border-white/[0.12] bg-white/[0.06] px-4 py-[9px] text-xs font-bold text-white transition-all duration-200 hover:border-white/[0.22] hover:bg-white/[0.12]"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
                {search.trim() && (
                  <button
                    className="cursor-pointer rounded-pill bg-white px-4 py-[9px] text-xs font-bold text-[#0a0a0d] transition-all duration-200 hover:-translate-y-px"
                    onClick={() => setCreateOpen(true)}
                  >
                    Create one →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {!isLoading && totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-3">
            <button
              type="button"
              aria-label="Previous page"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.055] text-white shadow-[0_14px_34px_rgba(0,0,0,0.24)] transition-all hover:-translate-y-0.5 hover:bg-white/[0.1] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:translate-y-0 disabled:hover:bg-white/[0.055]"
              onClick={() => goToPage(page - 1)}
              disabled={page === 1}
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
            </button>
            
            <div className="flex items-center gap-1.5 rounded-full bg-black/20 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-xl">
              {pageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  aria-label={`Page ${pageNumber}`}
                  aria-current={page === pageNumber ? 'page' : undefined}
                  className={cn(
                    'flex h-9 min-w-9 items-center justify-center rounded-full px-3 text-[14px] font-black transition-all',
                    page === pageNumber
                      ? 'scale-105 bg-white text-black shadow-[0_10px_28px_rgba(0,0,0,0.26)]'
                      : 'text-[#8f96a3] hover:bg-white/[0.055] hover:text-white',
                  )}
                  onClick={() => goToPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
            </div>

            <button
              type="button"
              aria-label="Next page"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.055] text-white shadow-[0_14px_34px_rgba(0,0,0,0.24)] transition-all hover:-translate-y-0.5 hover:bg-white/[0.1] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:translate-y-0 disabled:hover:bg-white/[0.055]"
              onClick={() => goToPage(page + 1)}
              disabled={page === totalPages}
            >
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        )}

        <CreateCommunityModal open={createOpen} onClose={() => setCreateOpen(false)} />
      </div>
    </div>
  );
}

/** "Sort by" dropdown for the directory header. */
function SortMenu({ value, onChange }: { value: SortId; onChange: (s: SortId) => void }) {
  const [open, setOpen] = useState(false);
  const activeLabel = SORTS.find((s) => s.id === value)?.label ?? 'Most active';

  return (
    <div className="flex items-center gap-2 text-[12px] font-semibold text-lo">
      <span>Sort by</span>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex items-center gap-1 bg-transparent text-[12px] font-black text-white transition-colors hover:text-accent"
        >
          {activeLabel}
          <ChevronDown
            size={14}
            strokeWidth={2.5}
            className={cn('ml-1 transition-transform', open && 'rotate-180')}
          />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div
              role="menu"
              className="absolute left-0 z-50 mt-3 w-48 bg-bg/95 p-1 shadow-[0_18px_48px_rgba(0,0,0,0.45)] backdrop-blur-2xl sm:left-auto sm:right-0"
            >
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={s.id === value}
                  onClick={() => {
                    onChange(s.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 bg-transparent px-3 py-2 text-left text-[13px] font-semibold text-mid transition-colors hover:text-hi"
                >
                  {s.label}
                  {s.id === value && <Check size={15} className="text-accent" />}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
